import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Check, CheckCheck, Clock, Loader2 } from 'lucide-react';
import { BRAND, arrowCss } from '@common/styles/brand-tokens';

/** Shared look of /onboarding: one calm column, one primary button per screen. */
export const C = {
  ...BRAND,
  bg: 'var(--color-bg, #f5f8fb)',
  warn: 'var(--connect-attention-fg, #92400e)',
  warnBg: 'color-mix(in srgb, #f59e0b 12%, var(--color-surface, #fff))',
  /** The one brand colour for everything interactive or active (solid; dark-mode aware). */
  ink: 'var(--onb-ink)',
  // Flat brand fills — no gradient, so every solid surface is the same single blue.
  brand: 'var(--onb-grad)',
  brandText: 'var(--onb-on-ink)',
  /** Its soft tint — icon wells, done markers, chips. */
  inkSoft: 'var(--onb-ink-soft)',
  /** The same sky blue, a shade deeper — only for small TEXT on light surfaces (legibility). */
  inkText: 'var(--onb-ink-text)',
};

export const ONB_CSS = `
.onb-root{--onb-wa-text:#128c4a;--onb-track:color-mix(in srgb,var(--color-text,#1a202c) 10%,transparent);--onb-on-ink:#fff;--onb-ink:#1ea1e8;--onb-ink-text:#0b84cc;--onb-ink-soft:color-mix(in srgb,#1ea1e8 13%,transparent);--onb-grad:linear-gradient(135deg,#4cc6f4 0%,#1d9be4 52%,#1281d2 100%);--onb-glow:rgba(26,147,223,.5);--cta-button-bg:var(--onb-ink)}
html[data-clear-flow] .onb-root{--onb-ink:var(--color-accent,#53bdeb);--onb-ink-text:var(--color-accent,#53bdeb);--onb-grad:var(--fab-bg);--onb-ink-soft:color-mix(in srgb,var(--color-accent,#53bdeb) 13%,transparent)}
.dark .onb-root{--onb-wa-text:#4ade80;--onb-on-ink:#fff;--onb-ink:#53bdeb;--onb-ink-text:#7dd3fc;--onb-grad:linear-gradient(135deg,#6fd2f7 0%,#33aceb 55%,#1c93dd 100%);--onb-glow:rgba(83,189,235,.45);--onb-ink-soft:color-mix(in srgb,#4dbdeb 16%,transparent)}
.onb-in{animation:onb-in .5s cubic-bezier(.2,.8,.2,1) both}
@keyframes onb-in{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
.onb-pop{animation:onb-pop .55s cubic-bezier(.2,1.5,.35,1) both}
@keyframes onb-pop{from{transform:scale(.3);opacity:0}to{transform:scale(1);opacity:1}}
.onb-cta{transition:transform .18s ease,box-shadow .18s ease,background-position .4s ease,filter .18s ease}
.onb-cta{background-size:140% 140%!important;background-position:0% 50%!important}
.onb-cta:hover:not(:disabled){transform:translateY(-2px);background-position:100% 50%!important;filter:saturate(1.12);box-shadow:0 16px 30px -12px var(--onb-glow)}
.onb-cta:active:not(:disabled){transform:none}
.onb-cta-wa{background-size:auto!important}
.onb-cta-wa svg:first-child{color:#25d366}
.onb-cta-wa:hover:not(:disabled){background:color-mix(in srgb,#25d366 9%,var(--color-surface,#fff))!important;filter:none;box-shadow:0 12px 24px -12px rgba(37,211,102,.6)!important}
.onb-cta:focus-visible{outline:2px solid var(--onb-ink);outline-offset:3px}
${arrowCss('onb-cta')}
${arrowCss('onb-choice')}
${arrowCss('onb-link')}
${arrowCss('onb-row')}
${arrowCss('onb-setup')}
.onb-choice{transition:border-color .15s ease,box-shadow .2s ease,transform .2s ease}
.onb-choice:hover:not(:disabled){border-color:color-mix(in srgb,var(--onb-ink) 70%,var(--color-border,#e5e7eb))!important;box-shadow:0 14px 34px -22px rgba(15,23,42,.45);transform:translateY(-2px)}
.onb-choice:focus-visible,.onb-ghost:focus-visible,.onb-link:focus-visible{outline:2px solid var(--onb-ink);outline-offset:3px}
.onb-ghost{transition:background .15s ease,border-color .15s ease}
.onb-ghost:hover:not(:disabled){background:color-mix(in srgb,var(--color-text,#1a202c) 5%,transparent)!important}
.onb-link{background:none;border:none;padding:0;cursor:pointer;font:inherit;color:var(--onb-ink-text);font-weight:600;display:inline-flex;align-items:center;gap:6px}
.onb-link:hover{text-decoration:underline}
.onb-row{transition:border-color .15s ease,background .15s ease}
.onb-row:hover{border-color:color-mix(in srgb,var(--onb-ink) 55%,transparent)!important;background:color-mix(in srgb,var(--onb-ink) 4%,transparent)!important}
.onb-row:focus-visible{outline:2px solid var(--onb-ink);outline-offset:3px}
.onb-setup{transition:transform .18s ease,box-shadow .18s ease}
.onb-setup:hover{transform:translateY(-1px);box-shadow:0 10px 22px -14px var(--onb-glow)}
.onb-setup:focus-visible{outline:2px solid var(--onb-ink);outline-offset:2px}
.onb-radar{position:relative}
.onb-win{position:relative;animation:onb-win .62s cubic-bezier(.2,1.6,.4,1) both}
@keyframes onb-win{0%{transform:scale(.35)}60%{transform:scale(1.2)}100%{transform:scale(1)}}
.onb-win::after{content:"";position:absolute;inset:-2px;border-radius:999px;border:2px solid var(--onb-ink);pointer-events:none;animation:onb-ring-out .85s ease-out .08s both}
@keyframes onb-ring-out{from{transform:scale(1);opacity:.75}to{transform:scale(2.3);opacity:0}}
.onb-spark{position:absolute;left:50%;top:50%;width:7px;height:7px;margin:-3.5px 0 0 -3.5px;border-radius:999px;background:var(--onb-ink);opacity:0;pointer-events:none;animation:onb-spark 1.05s cubic-bezier(.2,.7,.3,1) .05s both}
.onb-spark:nth-child(even){width:5px;height:5px;margin:-2.5px 0 0 -2.5px;background:color-mix(in srgb,var(--onb-ink) 45%,#fff)}
@keyframes onb-spark{0%{opacity:0;transform:rotate(var(--a)) translateY(-6px) scale(.5)}12%{opacity:1;transform:rotate(var(--a)) translateY(calc(var(--d) * -.35)) scale(1.1)}70%{opacity:.9;transform:rotate(var(--a)) translateY(calc(var(--d) * -.9)) scale(.8)}100%{opacity:0;transform:rotate(var(--a)) translateY(calc(var(--d) * -1.05)) scale(.4)}}
.onb-done-chip{display:inline-flex;align-items:center;gap:4px;margin-inline-start:10px;padding:2px 9px;border-radius:999px;background:var(--onb-ink-soft);color:var(--onb-ink-text);font-size:12px;font-weight:700;vertical-align:2px}
.onb-done-chip.is-new{animation:onb-chip-in .5s cubic-bezier(.2,1.5,.4,1) .25s both}
@keyframes onb-chip-in{from{opacity:0;transform:translateX(-6px) scale(.7)}to{opacity:1;transform:none}}
[dir=rtl] .onb-done-chip.is-new{animation-name:onb-chip-in-rtl}
@keyframes onb-chip-in-rtl{from{opacity:0;transform:translateX(6px) scale(.7)}to{opacity:1;transform:none}}
.onb-line{position:absolute;overflow:hidden;border-radius:2px;background:var(--onb-track)}
.onb-line>i{position:absolute;inset:0;background:var(--onb-grad);transform-origin:top;transition:transform .7s cubic-bezier(.4,0,.2,1) .2s}
.onb-radar::before,.onb-radar::after{content:"";position:absolute;inset:0;border-radius:999px;border:2px solid var(--onb-ink);animation:onb-radar 2.2s ease-out infinite}
.onb-radar::after{animation-delay:1.1s}
@keyframes onb-radar{from{transform:scale(1);opacity:.7}to{transform:scale(1.9);opacity:0}}
.onb-pulse{animation:onb-pulse 1.6s ease-out infinite}
@keyframes onb-pulse{0%{box-shadow:0 0 0 0 color-mix(in srgb,var(--onb-ink) 55%,transparent)}80%,100%{box-shadow:0 0 0 8px transparent}}
.onb-dots span{display:inline-block;animation:onb-dot 1.2s ease-in-out infinite}
.onb-dots span:nth-child(2){animation-delay:.15s}.onb-dots span:nth-child(3){animation-delay:.3s}
@keyframes onb-dot{0%,60%,100%{opacity:.25}30%{opacity:1}}
.onb-meter{transition:width .6s cubic-bezier(.2,.8,.2,1),background .4s ease}
.onb-chat{--chat:#efeae2;--out:#d9fdd3;--out-text:#111b21;--meta:#667781;--head:#008069}
.dark .onb-chat{--chat:#0b141a;--out:#005c4b;--out-text:#e9edef;--meta:#8696a0;--head:#202c33}
.onb-bubble-in{animation:onb-bubble .5s cubic-bezier(.2,1.3,.4,1) both}
@keyframes onb-bubble{from{opacity:0;transform:translateY(14px) scale(.96)}to{opacity:1;transform:none}}
.onb-ticks{animation:onb-pop .5s cubic-bezier(.2,1.5,.35,1) both}
.onb-editable{outline:1.5px dashed transparent;outline-offset:4px;transition:outline-color .15s ease;cursor:text}
.onb-editable:hover,.onb-editable:focus-within{outline-color:color-mix(in srgb,var(--onb-ink) 55%,transparent)}
.onb-textarea{width:100%;resize:none;border:none;outline:none;background:transparent;color:inherit;font:inherit;line-height:1.45;padding:0}
@media (max-width:720px){.onb-h1{font-size:25px!important}}
.onb-illo{--chat:#efeae2;--in:#ffffff;--out:#d9fdd3;--out-text:#111b21;--meta:#667781;--head:#008069;--line:rgba(17,27,33,.12)}
.dark .onb-illo{--chat:#0b141a;--in:#202c33;--out:#005c4b;--out-text:#e9edef;--meta:#8696a0;--head:#202c33;--line:rgba(233,237,239,.14)}
.onb-float{animation:onb-float 6s ease-in-out infinite}
@keyframes onb-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
.onb-a,.onb-b{grid-area:1/1;display:inline-flex;align-items:center;gap:4px}
.onb-a{animation:onb-a 5s ease-in-out infinite}
.onb-b{animation:onb-b 5s ease-in-out infinite}
@keyframes onb-a{0%,45%{opacity:1}55%,100%{opacity:0}}
@keyframes onb-b{0%,45%{opacity:0}55%,100%{opacity:1}}
@media (max-width:1199px){.onb-illo-slot{display:none!important}}
@media (prefers-reduced-motion:reduce){.onb-win,.onb-win::after,.onb-spark,.onb-done-chip.is-new,.onb-line>i,.onb-in,.onb-pop,.onb-radar::before,.onb-radar::after,.onb-pulse,.onb-dots span,.onb-bubble-in,.onb-ticks,.onb-float,.onb-a,.onb-b{animation:none}.onb-a{opacity:0}}
`;

export const PrimaryButton: React.FC<{
  /** 'whatsapp' = the one deliberate exception to the brand colour: a button that opens the real
   *  WhatsApp Web looks like WhatsApp, so nobody wonders where it leads. */
  tone?: 'brand' | 'whatsapp';
  onClick: () => void;
  disabled?: boolean;
  busy?: boolean;
  icon?: React.ReactNode;
  wide?: boolean;
  children: React.ReactNode;
}> = ({ tone = 'brand', onClick, disabled, busy, icon, wide, children }) => (
  <button
    type="button"
    className={tone === 'whatsapp' ? 'onb-cta onb-cta-wa' : 'onb-cta'}
    onClick={onClick}
    disabled={disabled || busy}
    style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 9, height: 48, padding: '0 24px', borderRadius: 12,
      border: tone === 'whatsapp' ? '2px solid #25d366' : 'none', background: tone === 'whatsapp' ? C.surface : C.brand, color: tone === 'whatsapp' ? 'var(--onb-wa-text)' : C.brandText, fontSize: 15.5, fontWeight: 700, fontFamily: 'inherit',
      cursor: disabled || busy ? 'default' : 'pointer', opacity: disabled ? 0.55 : 1, whiteSpace: 'nowrap',
      width: wide ? '100%' : undefined, boxShadow: tone === 'whatsapp' ? '0 8px 18px -12px rgba(37,211,102,.55)' : '0 10px 22px -10px var(--onb-glow)',
    }}
  >
    {busy ? <Loader2 size={18} className="animate-spin" /> : icon}
    {children}
    {!busy && <ArrowRight size={17} strokeWidth={2.5} className="bt-arrow" />}
  </button>
);

export const GhostButton: React.FC<{ onClick: () => void; icon?: React.ReactNode; children: React.ReactNode }> = ({ onClick, icon, children }) => (
  <button
    type="button"
    className="onb-ghost"
    onClick={onClick}
    style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, height: 48, padding: '0 20px', borderRadius: 12,
      border: `1px solid ${C.border}`, background: 'transparent', color: C.text, fontSize: 15, fontWeight: 600, fontFamily: 'inherit',
      cursor: 'pointer', whiteSpace: 'nowrap',
    }}
  >
    {icon}
    {children}
  </button>
);

export const Heading: React.FC<{ title: string; body?: React.ReactNode; center?: boolean }> = ({ title, body, center }) => (
  <div style={{ textAlign: center ? 'center' : 'start' }}>
    <h1 className="onb-h1" style={{ margin: 0, fontSize: 30, fontWeight: 750, letterSpacing: '-.02em', lineHeight: 1.2, color: C.text }}>{title}</h1>
    {body && <p style={{ margin: '10px 0 0', fontSize: 16, lineHeight: 1.55, color: C.muted }}>{body}</p>}
  </div>
);

export const Notice: React.FC<{ icon: React.ReactNode; children: React.ReactNode; action?: React.ReactNode }> = ({ icon, children, action }) => (
  <div role="status" className="onb-in" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 12, background: C.warnBg, color: C.warn, fontSize: 14, lineHeight: 1.5 }}>
    <span aria-hidden style={{ display: 'flex', flex: '0 0 auto' }}>{icon}</span>
    <span style={{ flex: '1 1 220px', minWidth: 0 }}>{children}</span>
    {action}
  </div>
);

export type StepState = 'todo' | 'current' | 'done';

/** One row of a vertical path: numbered dot, title, body, and — only on the current row — its button. */
/** True for a moment after `state` turns 'done' while on screen — never for a step that was
 *  already done when the page loaded, so the celebration always means "you just did that". */
export function useJustCompleted(state: StepState): boolean {
  const prev = useRef(state);
  const [fresh, setFresh] = useState(false);
  useEffect(() => {
    const was = prev.current;
    prev.current = state;
    if (state !== 'done' || was === 'done') return undefined;
    setFresh(true);
    const id = window.setTimeout(() => setFresh(false), 1800);
    return () => window.clearTimeout(id);
  }, [state]);
  return fresh;
}

const SPARKS = [0, 45, 90, 135, 180, 225, 270, 315];

/** The step marker: number → pulsing current → a solid check that bursts when it's earned. */
export const StepMark: React.FC<{ n: number; state: StepState; size?: number }> = ({ n, state, size = 40 }) => {
  const fresh = useJustCompleted(state);
  const done = state === 'done';
  return (
    <span
      aria-hidden
      key={state}
      className={state === 'current' ? 'onb-radar' : done && fresh ? 'onb-win' : undefined}
      style={{
        position: 'relative', width: size, height: size, borderRadius: 999, flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: size > 30 ? 15 : 11.5, fontWeight: 750, boxSizing: 'border-box',
        background: done ? C.brand : C.surface,
        color: done ? C.brandText : state === 'current' ? C.inkText : C.muted,
        border: done ? 'none' : `${size > 30 ? 2.5 : 2}px solid ${state === 'current' ? C.ink : C.trackStrong}`,
        boxShadow: done ? '0 6px 14px -6px var(--onb-glow)' : undefined,
      }}
    >
      {done ? <Check size={size > 30 ? 20 : 13} strokeWidth={3.2} /> : n}
      {done && fresh && SPARKS.map((a, i) => (
        <i key={a} className="onb-spark" style={{ ['--a' as string]: `${a + (i % 2 ? 12 : 0)}deg`, ['--d' as string]: `${size * (i % 2 ? 0.7 : 0.95)}px` } as React.CSSProperties} />
      ))}
    </span>
  );
};

export const PathStep: React.FC<{ n: number; state: StepState; title: string; body: React.ReactNode; action?: React.ReactNode; last?: boolean; doneLabel?: string }> = ({ n, state, title, body, action, last, doneLabel }) => {
  const fresh = useJustCompleted(state);
  return (
  <li aria-current={state === 'current' ? 'step' : undefined} style={{ display: 'flex', gap: 16, position: 'relative', paddingBottom: last ? 0 : 26 }}>
    {!last && (
      // The connector fills toward the next step the moment this one is done.
      <span aria-hidden className="onb-line" style={{ insetInlineStart: 19, top: 46, bottom: 6, width: 2 }}>
        <i style={{ transform: state === 'done' ? 'scaleY(1)' : 'scaleY(0)' }} />
      </span>
    )}
    <StepMark n={n} state={state} />
    <div style={{ flex: 1, minWidth: 0, paddingTop: 8 }}>
      <div style={{ fontSize: 16.5, fontWeight: 650, color: state === 'todo' ? C.muted : C.text }}>
        {title}
        {state === 'done' && doneLabel && <span className={fresh ? 'onb-done-chip is-new' : 'onb-done-chip'}><Check size={12} strokeWidth={3} />{doneLabel}</span>}
      </div>
      <div style={{ fontSize: 14.5, color: C.muted, marginTop: 4, lineHeight: 1.55 }}>{body}</div>
      {action && <div style={{ marginTop: 16 }}>{action}</div>}
    </div>
  </li>
  );
};

export const WaitingChip: React.FC<{ label: string }> = ({ label }) => (
  <span role="status" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontSize: 14, fontWeight: 600, color: C.text, padding: '9px 15px', borderRadius: 999, background: C.inkSoft, border: '1px solid color-mix(in srgb, var(--onb-ink) 30%, transparent)' }}>
    <span aria-hidden className="onb-pulse" style={{ width: 9, height: 9, borderRadius: 999, background: C.ink }} />
    {label}
    <span className="onb-dots" aria-hidden><span>.</span><span>.</span><span>.</span></span>
  </span>
);

/** A quiet secondary option — dashed, one line of text and a link-style action — under the one
 *  recommended path (always-on under "this computer", the free computer path under always-on…). */
export const OptionRow: React.FC<{ icon: React.ReactNode; title: string; badge?: string; body: string; cta: string; busy?: boolean; ctaButton?: boolean; onClick: () => void }> = ({ icon, title, badge, body, cta, busy, ctaButton, onClick }) => (
  <button
    type="button"
    className="onb-row"
    onClick={onClick}
    disabled={busy}
    style={{
      width: '100%', display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 14, rowGap: 8, textAlign: 'start', padding: '14px 16px',
      borderRadius: 14, border: `1px dashed ${C.trackStrong}`, background: 'transparent', cursor: busy ? 'default' : 'pointer', fontFamily: 'inherit',
    }}
  >
    <span aria-hidden style={{ width: 38, height: 38, borderRadius: 11, flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.track, color: C.text }}>
      {icon}
    </span>
    <span style={{ flex: '1 1 260px', minWidth: 0 }}>
      <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, fontSize: 14.5, fontWeight: 600, color: C.text }}>
        {title}
        {badge && <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.06em', padding: '2px 7px', borderRadius: 999, background: C.warnBg, color: C.warn }}>{badge}</span>}
      </span>
      <span style={{ display: 'block', fontSize: 13.5, color: C.muted, marginTop: 2 }}>{body}</span>
    </span>
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13.5, fontWeight: 650, whiteSpace: 'nowrap', marginInlineStart: 52,
        // ctaButton: the row's action reads as a real button (filled pill), not a trailing text link.
        ...(ctaButton ? { padding: '8px 14px', borderRadius: 999, background: C.brand, color: C.brandText } : { color: C.inkText }),
      }}
    >
      {cta}{busy ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} className="bt-arrow" />}
    </span>
  </button>
);

/** The wizard's progress — lives inside ConnectFlow so it travels with it to every surface. */
export const Progress: React.FC<{ pct: number; step: 1 | 2 | 3; done: boolean; labels: [string, string, string]; meterLabel: string; ariaLabel: string }> = ({ pct, step, done, labels, meterLabel, ariaLabel }) => (
  <div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div role="progressbar" aria-label={ariaLabel} aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} style={{ flex: 1, height: 6, borderRadius: 999, background: C.track, overflow: 'hidden' }}>
        <div className="onb-meter" style={{ height: '100%', width: `${pct}%`, borderRadius: 999, background: done ? C.success : C.brand }} />
      </div>
      <span style={{ flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, fontWeight: 700, color: done ? C.success : C.muted, whiteSpace: 'nowrap' }}>
        {done && <Check size={13} strokeWidth={3} />}{meterLabel}
      </span>
    </div>
    <ol style={{ listStyle: 'none', margin: '10px 0 0', padding: 0, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
      {labels.map((label, i) => {
        const n = i + 1;
        const state = done || n < step ? 'done' : n === step ? 'current' : 'todo';
        return (
          <li key={label} aria-current={state === 'current' ? 'step' : undefined} style={{ display: 'flex', alignItems: 'center', justifyContent: i === 0 ? 'flex-start' : i === 2 ? 'flex-end' : 'center', gap: 5, fontSize: 12.5, fontWeight: state === 'current' ? 750 : 550, color: state === 'todo' ? C.muted : C.text }}>
            {state === 'done' && <Check size={12} strokeWidth={3} style={{ color: C.ink }} />}{label}
          </li>
        );
      })}
    </ol>
  </div>
);

const Chip: React.FC<{ bg: string; children: React.ReactNode }> = ({ bg, children }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 999, background: bg, color: '#fff', fontSize: 12.5, fontWeight: 700, boxShadow: '0 10px 20px -10px rgba(15,23,42,.6)' }}>{children}</span>
);

/** A live miniature of what they're about to get: a message going from scheduled (clock) to
 *  delivered (blue ticks), on a loop. */
export const PreviewIllustration: React.FC<{ message: string; scheduled: string; delivered: string; scheduledChip: string }> = ({ message, scheduled, delivered, scheduledChip }) => (
  <div aria-hidden className="onb-illo onb-float" style={{ position: 'relative', width: 340 }}>
    <div style={{ position: 'relative', borderRadius: 20, overflow: 'hidden', border: `1px solid ${C.border}`, background: 'var(--chat)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 16px', background: 'var(--head)' }}>
        <span style={{ width: 32, height: 32, borderRadius: 999, background: 'rgba(255,255,255,.28)' }} />
        <span style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span style={{ width: 96, height: 8, borderRadius: 4, background: 'rgba(255,255,255,.85)' }} />
          <span style={{ width: 58, height: 6, borderRadius: 4, background: 'rgba(255,255,255,.5)' }} />
        </span>
      </div>
      <div style={{ padding: '22px 16px 26px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ alignSelf: 'flex-start', padding: '9px 11px', borderRadius: '12px 12px 12px 3px', background: 'var(--in)' }}>
          <span style={{ display: 'block', width: 132, height: 7, borderRadius: 4, background: 'var(--line)' }} />
          <span style={{ display: 'block', width: 88, height: 7, borderRadius: 4, background: 'var(--line)', marginTop: 7 }} />
        </div>
        <div style={{ alignSelf: 'flex-end', maxWidth: '86%', padding: '9px 11px 7px', borderRadius: '12px 12px 3px 12px', background: 'var(--out)', color: 'var(--out-text)' }}>
          <div style={{ fontSize: 13.5, lineHeight: 1.45 }}>{message}</div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 5, fontSize: 11, color: 'var(--meta)' }}>
            <span style={{ display: 'inline-grid' }}>
              <span className="onb-a"><Clock size={11} strokeWidth={2.5} />{scheduled}</span>
              <span className="onb-b">{delivered}<CheckCheck size={14} strokeWidth={2.5} style={{ color: '#53bdeb' }} /></span>
            </span>
          </div>
        </div>
      </div>
    </div>
    <span className="onb-pop" style={{ position: 'absolute', top: -14, insetInlineEnd: -12, animationDelay: '.35s' }}>
      <span style={{ display: 'inline-grid' }}>
        <span className="onb-a"><Chip bg="#1677cc"><Clock size={13} strokeWidth={2.5} />{scheduledChip}</Chip></span>
        <span className="onb-b"><Chip bg={C.success}><CheckCheck size={14} strokeWidth={2.5} />{delivered}</Chip></span>
      </span>
    </span>
  </div>
);

/** The card surface of /connections (the wizard / set-up state, and the connections list).
 *  Carries the scoped styles. */
export const ConnectCard: React.FC<{ dir: 'ltr' | 'rtl'; children: React.ReactNode }> = ({ dir, children }) => (
  <div className="onb-root" dir={dir}>
    <style>{ONB_CSS}</style>
    <section className="onb-in" style={{ position: 'relative', borderRadius: 14, background: C.surface, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
      <div style={{ padding: 24 }}>{children}</div>
    </section>
  </div>
);
